import { createSlice, createSelector } from '@reduxjs/toolkit'
import { DateTime } from 'luxon'

import { createApiAsyncThunk } from '@/services/apiService'
import { setTimeoutInDev } from '@/utils/environment'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { sanitizeFormData } from '@/utils/formData'
import { updateCurrentBien } from '../biens/biensSlice'
import { hasFiles, transformToFormData } from '../../utils/formData'
import { FORM_TYPES } from './LocationFormContext'

export const locationTypes = {
  classique: "classique",
  meuble: "meublé",
  civil: "droit civil", 
  courte: "courte durée"
}

export const locationFormRules = (getValues) => ({
  type_location: { required: "Le type de la location est requis" },
  date_fin_bail: {
    validate: (value) => {
      if(!value) return;
      const date1 = DateTime.fromISO(getValues('date_effet_bail'))
      const date2 = DateTime.fromISO(value)
      return date2 > date1 || 'doit être postérieure à la date d\'effet de bail';
    }
  }
})

const URL = 'locations'
const getUrl = (bien_id, id) => {
  const url = `biens/${bien_id}/${URL}`
  if(id) {
    return `${url}/${id}`
  }
  return url
}

// États initiaux
const initialState = {
  items: [],
  current: null,
  inEdition: null,
  loadingStatus: {
    fetchList: false,
    create: false,
    update: false,
    delete: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {}
  },
  filters: {
    page: 1,
    limit: 5 
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  },
}

const sanitizeLocationFormData = (data) => {
  const formData = sanitizeFormData(data)
  delete formData.bien_id

  formData.locataires = formData.locataires?.map(
    prop => ({contact_id: prop.id})
  )
  delete formData.montant_charges_total
  delete formData.active
  delete formData.prix_loyer_charges_comprises
  delete formData.prix_net_proprietaire
  delete formData.status_for_locataire
  delete formData.quittances_count

  let result
  if(hasFiles(
    formData
  )){
    result = transformToFormData(formData,"location")
  } 
  return result ? result : {location: formData}
}

// Thunks
export const fetchLocations = createApiAsyncThunk(
  'locations/fetchList',
  async (params = {}, { api, rejectWithValue }) => {
    try {
      const bien_id = params.bien_id
      delete params.bien_id

      await setTimeoutInDev()
      const response = await api
        .url(getUrl(bien_id))
        .query(params)
        .get()
      return response
    } catch (error) {
      console.error('Error fetching locations:', error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des locations')
    }
  }
)

const getSuccessNotification = (formType) => {
  let message
  switch(formType) {
    case FORM_TYPES.EDIT:
      message = "Détails de la location mis à jour avec succès"
      break
    case FORM_TYPES.NEW:
      message = "Location créée avec succès"
      break
    case FORM_TYPES.CLOSE:
      message = "Location mise à jour avec succès"
      break
    default:
      message = "Location mise à jour avec succès"
  }
  return addFlashMessage({type: 'success', text: message})
}

export const createLocation = createApiAsyncThunk(
  'locations/create',
  async (params, { api, dispatch, rejectWithValue }) => {
    const { bien_id, formType, data} = params
    try {
      if(!bien_id) throw new Error("Une location doit être attachée à un bien")

      const response = await api
        .url(getUrl(bien_id))
        .post(sanitizeLocationFormData(data))

      dispatch(setCurrentLocation(response))
      dispatch(updateCurrentBien({current_location: response}))
      
      switch(formType) {
        case FORM_TYPES.EDIT:
          dispatch(addFlashMessage({type: 'success', text: "Détails de la location mis à jour avec succès"}))
          break
        case FORM_TYPES.NEW:
          dispatch(addFlashMessage({type: 'success', text: "Location créée avec succès"}))
          break
      }

      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updateLocation = createApiAsyncThunk(
  'locations/update',
  async ({ bien_id, id, data, formType }, { api, dispatch, getState, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(bien_id, id))
        .put(sanitizeLocationFormData(data))
      
      // Met à jour la location actuelle seulement si c'est celle qui a été modifiée.
      // aka la location active
      if(getState().locations?.current?.id === response.id) {
        dispatch(setCurrentLocation(response))
        dispatch(updateCurrentBien({current_location: response}))
      }

      dispatch(getSuccessNotification(formType))

      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const deleteLocation = createApiAsyncThunk(
  'locations/delete',
  async (location, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(location.bien_id, location.id))
        .delete()
      dispatch(addFlashMessage({type: 'success', text: "La location a été supprimée avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

// Valeurs par défaut 
export const emptyLocation = {
  bien_id: null,
  type_location:	"",
  prix_loyer: "",
  montant_honoraires_ttc:	"",
  montant_frais_entree: "",
  montant_gli: "",
  date_effet_bail: "",
  date_fin_bail: "",
  montant_depot_garantie: "",
  montant_charges_syndic: "",
  montant_charges_eau_froide: "",
  montant_charges_menage_commun: "",
  montant_charges_ordures_menageres: "",
  montant_charges_entretien_chauffage: "",
  montant_charges_espaces_verts: "",
  depot_garantie_conserve_agence:	false,
  locataires: []
}

const sanitizesPayload = (action) => Object.fromEntries(
  Object.entries(action.payload).map(([key, value]) =>
    [key, value === null ? (emptyLocation[key] || "") : value]
  )
)

// Slice
const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    setItems: (state,action) => {
      state.items = action.payload
    },
    setEmptyCurrentLocation: (state, action) => {
      const data = {
        ...emptyLocation,
        ...action.payload
      }
      state.current = {
        ...data
      }
      state.requestStatus = initialState.requestStatus
    },
    setCurrentLocation: (state, action) => {
      const sanitizedData = {
        ...emptyLocation,
        ...sanitizesPayload(action)
      }
      state.current = {
        ...sanitizedData
      }
      state.requestStatus = initialState.requestStatus
    },
    setLocationInEdition: (state, action) => {
      state.inEdition = {
        ...emptyLocation,
        ...action.payload
      }
      state.requestStatus = initialState.requestStatus
    },
    resetCurrentLocation: (state) => {
      state.current = initialState.current
      state.requestStatus = initialState.requestStatus
    },
    resetLocationInEdition: (state) => {
      state.inEdition = initialState.inEdition
      state.requestStatus = initialState.requestStatus
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    },
  },
  extraReducers: (builder) => {
    // fetchLocations
    builder.addCase(fetchLocations.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchLocations.fulfilled, (state, action) => {
      state.items = action.payload.locations
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchLocations.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // createLocation
    builder.addCase(createLocation.pending, (state) => {
      state.loadingStatus.create = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createLocation.fulfilled, (state, action) => {
      state.items.unshift(action.payload)
      state.loadingStatus.create = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(createLocation.rejected, (state, action) => {
      state.loadingStatus.create = false
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
    })

    // updateLocation
    builder.addCase(updateLocation.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updateLocation.fulfilled, (state, action) => {
      const updated = action.payload
      const index = state.items.findIndex(item => item.id === updated.id)
      if (index !== -1) {
        state.items[index] = updated
      }
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updateLocation.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })
 
    // Delete Location
    builder.addCase(deleteLocation.pending, (state) => {
      state.loadingStatus.delete = true
      state.requestStatus.error = null
    })
    builder.addCase(deleteLocation.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      const index = state.items.findIndex( item => item.id === action.payload.id)
      if (index !== -1) {
        state.items.splice(index, 1)
      }
      state.requestStatus.success = true
    })
    builder.addCase(deleteLocation.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })
  }
})

// Tools
export const isRegisteredLocation = (location) => 
  location.id && location.locataires?.length > 0

const FielsToReset = [
  "montant_premier_loyer_prorata", "premier_loyer_recu",
  "frais_entree_recu",
  "depot_garantie_recu",
  "irl",
  "montant_loyer_solde_sortie", "loyer_solde_recu",
  "date_effet_bail", "date_etat_lieux_entree", 
  "date_fin_bail", "date_etat_lieux_sortie",
  "bail_file", "etat_lieux_entree_file", "etat_lieux_sortie_file"
]

export const setNewLocation = (location)  => {
  if(isRegisteredLocation) {
    const { id, ...newLocation 
    } = location
    newLocation.locataires = null
    FielsToReset.forEach( field => newLocation[field] = null )
    return newLocation
  } else {
    return location
  }
}

// Sélecteurs
export const selectRegisteredLocations = createSelector(
  [state => state.locations.items],
  (locations) => locations.filter(isRegisteredLocation)
)

export const { 
  setEmptyCurrentLocation,
  setCurrentLocation,
  setItems,
  setLocationInEdition,
  resetCurrentLocation,
  resetLocationInEdition,
  setFilters,
  resetRequestStatus,
} = locationsSlice.actions

export default locationsSlice.reducer